import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import AppLogo from '@/components/AppLogo';
import { onboardingApi } from '@/lib/api';
import Step1BasicInfo from './Step1BasicInfo';
import Step2Documents from './Step2Documents';
import Step3HFR      from './Step3HFR';
import Step4Modules  from './Step4Modules';
import Step5GoLive   from './Step5GoLive';

const SESSION_KEY = 'as_ob_session';

const STEPS = [
  { n: 1, label: 'Basic Info',    desc: 'Hospital details'        },
  { n: 2, label: 'Verification',  desc: 'Document upload'         },
  { n: 3, label: 'HFR',           desc: 'ABDM linkage (optional)' },
  { n: 4, label: 'Modules',       desc: 'Plan & admin account'    },
  { n: 5, label: 'Go Live',       desc: 'Provision & launch'      },
];

interface Session {
  tenantId: string;
  token:    string;
}

interface ProvisionData {
  plan:          string;
  adminEmail:    string;
  adminName:     string;
  adminPassword: string;
}

interface OnboardingPageProps {
  autoStart?: boolean;
}

export default function OnboardingPage({ autoStart = false }: OnboardingPageProps) {
  const [session, setSession]           = useState<Session | null>(null);
  const [step, setStep]                 = useState(1);
  const [tenantSlug, setTenantSlug]     = useState('');
  const [provisionData, setProvisionData] = useState<ProvisionData | null>(null);
  const [starting, setStarting]         = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        const s = JSON.parse(raw) as Session & { step?: number; tenantSlug?: string };
        setSession({ tenantId: s.tenantId, token: s.token });
        setStep(s.step ?? 1);
        setTenantSlug(s.tenantSlug ?? '');
        return;
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }
    if (autoStart) startOnboarding();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveSession(s: Session, currentStep: number, slug?: string) {
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      ...s, step: currentStep, tenantSlug: slug ?? tenantSlug,
    }));
  }

  async function startOnboarding() {
    setStarting(true);
    try {
      const { data } = await onboardingApi.start();
      const s = { tenantId: data.tenantId, token: data.token };
      setSession(s);
      setStep(1);
      saveSession(s, 1);
    } finally {
      setStarting(false);
    }
  }

  function goTo(n: number) {
    setStep(n);
    if (session) saveSession(session, n);
  }

  function onStep1Next(slug: string) {
    setTenantSlug(slug);
    if (session) saveSession(session, 2, slug);
    setStep(2);
  }

  function onStep2Next() { goTo(3); }
  function onStep3Next() { goTo(4); }

  function onStep4Next(plan: string, admin: { adminName: string; adminEmail: string; adminPassword: string }) {
    setProvisionData({ plan, ...admin });
    goTo(5);
  }

  // ── Landing screen (no session yet, non-autoStart) ─────────────
  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow-card-md p-10 w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-2xl bg-navy flex items-center justify-center">
              <AppLogo size={36} inverted/>
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-navy">Register a hospital</h1>
            <p className="text-gray-400 text-sm mt-2">5-step setup · Takes about 10 minutes</p>
          </div>
          <ul className="text-left space-y-2">
            {STEPS.map(s => (
              <li key={s.n} className="flex items-center gap-3 text-sm text-gray-500">
                <span className="w-6 h-6 rounded-full bg-navy/10 text-navy text-xs font-bold
                                 flex items-center justify-center flex-shrink-0">{s.n}</span>
                <span><strong className="text-navy">{s.label}</strong> — {s.desc}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={startOnboarding}
            disabled={starting}
            className="w-full py-3 rounded-lg bg-orange text-navy font-semibold text-sm
                       hover:bg-orange-dark transition-colors disabled:opacity-50">
            {starting ? 'Starting…' : 'Start Registration →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Main wizard ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-navy flex items-center justify-center">
          <AppLogo size={20} inverted/>
        </div>
        <div>
          <div className="text-navy font-semibold text-sm leading-tight">Aarogya Saarthi</div>
          <div className="text-gray-400 text-xs">Hospital Registration</div>
        </div>
        <div className="ml-auto text-xs text-gray-400 hidden sm:block">
          DPDP-compliant · ABDM-ready
        </div>
      </div>

      {/* ── Horizontal step bar ──────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 px-6 py-5 flex-shrink-0">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start">
            {STEPS.map((s, i) => {
              const done    = step > s.n;
              const current = step === s.n;
              return (
                <div key={s.n} className="flex items-start flex-1">
                  {/* Step pill */}
                  <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: 56 }}>
                    <div className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold',
                      'border-2 transition-all duration-200',
                      done    ? 'bg-clinical-success border-clinical-success text-white' :
                      current ? 'bg-orange border-orange text-navy' :
                                'bg-white border-gray-200 text-gray-300'
                    )}>
                      {done ? <Check size={14}/> : s.n}
                    </div>
                    <div className={clsx(
                      'text-center mt-1.5 leading-tight',
                      current ? 'text-navy font-semibold' : done ? 'text-gray-500' : 'text-gray-300'
                    )}>
                      <div className="text-xs">{s.label}</div>
                      {s.n === 3 && (
                        <div className="text-2xs opacity-60">optional</div>
                      )}
                    </div>
                  </div>

                  {/* Connector line — only between steps */}
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 mt-4 mx-1">
                      <div className={clsx(
                        'h-0.5 w-full rounded-full transition-all duration-300',
                        step > s.n ? 'bg-clinical-success' : 'bg-gray-200'
                      )}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Step content ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-card-md p-8">

            {/* Step header */}
            <div className="mb-6 pb-5 border-b border-gray-100">
              <div className="text-xs font-semibold text-orange uppercase tracking-widest mb-1">
                Step {step} of 5
              </div>
              <h2 className="text-xl font-bold text-navy">{STEPS[step - 1].label}</h2>
              <p className="text-gray-400 text-sm mt-0.5">{STEPS[step - 1].desc}</p>
            </div>

            {step === 1 && (
              <Step1BasicInfo
                tenantId={session.tenantId}
                token={session.token}
                onNext={onStep1Next}
              />
            )}
            {step === 2 && (
              <Step2Documents
                tenantId={session.tenantId}
                token={session.token}
                onNext={onStep2Next}
                onBack={() => goTo(1)}
              />
            )}
            {step === 3 && (
              <Step3HFR
                tenantId={session.tenantId}
                token={session.token}
                onNext={onStep3Next}
                onBack={() => goTo(2)}
              />
            )}
            {step === 4 && (
              <Step4Modules
                onNext={onStep4Next}
                onBack={() => goTo(3)}
              />
            )}
            {step === 5 && provisionData && (
              <Step5GoLive
                tenantId={session.tenantId}
                token={session.token}
                tenantSlug={tenantSlug}
                plan={provisionData.plan}
                adminEmail={provisionData.adminEmail}
                adminName={provisionData.adminName}
                adminPassword={provisionData.adminPassword}
                onBack={() => goTo(4)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
